
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Youtube, Loader2 } from 'lucide-react';
import { initiateYouTubeAuth } from '@/services/youtube/auth/authorize';

interface YouTubeAuthProps {
  onAuthSuccess: (accessToken: string, refreshToken: string, expiry: number) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
}

const YouTubeAuth = ({ 
  onAuthSuccess, 
  onError, 
  onCancel, 
  isLoading,
  setLoading 
}: YouTubeAuthProps) => {
  
  const handleConnectYouTube = () => {
    try {
      setLoading(true);
      // Store user email if available for display in selection screen
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        // Attempt to extract email from user context if available 
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.email) {
          localStorage.setItem('userEmail', user.email);
        }
      }
      
      initiateYouTubeAuth();
    } catch (error) {
      console.error('Failed to initiate YouTube auth:', error);
      setLoading(false);
      onError(error instanceof Error ? error.message : 'Failed to connect to YouTube');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center">
        <Button variant="ghost" size="icon" onClick={onCancel} className="mr-2">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-medium">Connect YouTube</h2>
      </div>
      
      <div className="p-6">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="bg-red-100 text-red-600 p-4 rounded-full mb-4">
            <Youtube className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-medium mb-2">Connect your YouTube channel</h3>
          <p className="text-center text-gray-600 mb-6 max-w-md">
            Connect your YouTube channel to view and respond to comments, manage videos, and track performance.
          </p>
          
          <Button 
            onClick={handleConnectYouTube}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Connecting...
              </>
            ) : (
              <>Connect YouTube</>
            )}
          </Button>
          
          <div className="mt-6 text-sm text-gray-500">
            <p>We'll redirect you to Google to authorize access to your YouTube channel.</p>
          </div>
        </div>
        
        <div className="pt-6 flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default YouTubeAuth;
